import { api } from "../api";
import { openModal, modalBody } from "../components/modal";
import type { PracticePlanningAssignment, PracticeOrgAssignmentSteckbrief } from "../types";
import { esc } from "../utils/html";

function fmtDate(iso: string): string {
  if (!iso) return "";
  // Postgres DATE landet via pg + JSON.stringify als "YYYY-MM-DDTHH:mm:ss.sssZ".
  // Nur den Datums-Anteil verwenden – sonst gibt's Zeitzonen-Verschiebungen.
  const datePart = iso.slice(0, 10);
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d, 12, 0, 0).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function isoDate(d: Date): string {
  // Lokale Datums-Komponenten (keine UTC-Konvertierung – sonst springt das Datum bei Zeitzonen)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseIso(iso: string): Date {
  // Mittags, damit DST-Übergänge nicht zu Tages-Sprüngen führen
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
}

function mondayOfWeek(d: Date): Date {
  const day = d.getDay() || 7; // Sonntag → 7
  const monday = new Date(d);
  monday.setHours(12, 0, 0, 0);
  monday.setDate(d.getDate() - (day - 1));
  return monday;
}

function sundayOfWeek(d: Date): Date {
  const monday = mondayOfWeek(d);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return sunday;
}

function isoWeekNumber(d: Date): number {
  // ISO-Wochennummer (Montag = erster Tag, Donnerstag entscheidet)
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

function defaultRange(): { from: string; to: string } {
  // Default: Montag der aktuellen KW bis Sonntag von KW+11 (insgesamt 12 Kalenderwochen)
  const today = new Date();
  const from = mondayOfWeek(today);
  const toMonday = new Date(from);
  toMonday.setDate(from.getDate() + 11 * 7);
  const to = sundayOfWeek(toMonday);
  return { from: isoDate(from), to: isoDate(to) };
}

let currentFrom = defaultRange().from;
let currentTo = defaultRange().to;

export async function render(el: HTMLElement) {
  // Range bei jedem Page-Aufruf zurücksetzen, damit der Default greift, wenn der User die Seite frisch öffnet.
  const def = defaultRange();
  currentFrom = def.from;
  currentTo = def.to;

  el.innerHTML = `
    <div class="page-container">
      <div class="page-header"><h1>Praktikumsplanung</h1></div>
      <div class="pp-range-filter" id="pp-filters"></div>
      <div id="pp-content"><div class="spinner"></div></div>
    </div>`;

  renderFilterRow(el);
  await loadAndRender(el);
}

function renderFilterRow(root: HTMLElement) {
  const filterRow = root.querySelector("#pp-filters") as HTMLElement;
  const fromKw = isoWeekNumber(parseIso(currentFrom));
  const toKw = isoWeekNumber(parseIso(currentTo));
  filterRow.innerHTML = `
    <div class="pp-range-group">
      <label>Von</label>
      <input type="date" id="pp-from" value="${currentFrom}" />
      <span class="pp-kw-hint">KW ${fromKw} · ${esc(fmtDate(currentFrom))}</span>
    </div>
    <div class="pp-range-group">
      <label>Bis</label>
      <input type="date" id="pp-to" value="${currentTo}" />
      <span class="pp-kw-hint">KW ${toKw} · ${esc(fmtDate(currentTo))}</span>
    </div>
    <button type="button" class="btn btn-secondary btn-sm" id="pp-reset">Zurücksetzen</button>
  `;

  const fromInput = filterRow.querySelector("#pp-from") as HTMLInputElement;
  const toInput = filterRow.querySelector("#pp-to") as HTMLInputElement;
  const resetBtn = filterRow.querySelector("#pp-reset") as HTMLButtonElement;

  fromInput.addEventListener("change", async () => {
    if (!fromInput.value) return;
    const snapped = mondayOfWeek(parseIso(fromInput.value));
    currentFrom = isoDate(snapped);
    // Wenn neues Von nach Bis liegt → Bis auf Sonntag derselben Woche
    if (currentFrom > currentTo) currentTo = isoDate(sundayOfWeek(snapped));
    renderFilterRow(root);
    await loadAndRender(root);
  });

  toInput.addEventListener("change", async () => {
    if (!toInput.value) return;
    const snapped = sundayOfWeek(parseIso(toInput.value));
    currentTo = isoDate(snapped);
    if (currentTo < currentFrom) currentFrom = isoDate(mondayOfWeek(snapped));
    renderFilterRow(root);
    await loadAndRender(root);
  });

  resetBtn.addEventListener("click", async () => {
    const def = defaultRange();
    currentFrom = def.from;
    currentTo = def.to;
    renderFilterRow(root);
    await loadAndRender(root);
  });
}

async function loadAndRender(root: HTMLElement) {
  const content = root.querySelector("#pp-content") as HTMLElement;
  content.innerHTML = `<div class="spinner"></div>`;

  const from = currentFrom, to = currentTo;

  let resp: { from: string; to: string; items: PracticePlanningAssignment[] };
  let linkedSchoolsCount = 0;
  try {
    const [data, me] = await Promise.all([
      api.practiceOrg.planung(from, to),
      api.practiceOrg.me(),
    ]);
    resp = data;
    linkedSchoolsCount = me.linkedSchools?.length ?? 0;
  } catch (err: any) {
    content.innerHTML = `<div class="alert alert-error">${esc(err.message || "Planung konnte nicht geladen werden.")}</div>`;
    return;
  }

  if (linkedSchoolsCount === 0) {
    content.innerHTML = `
      <div class="empty-state empty-state--inline">
        <p style="margin:0">
          Noch keine Schule hat dich verknüpft. Gib deinen Einrichtungs-Code an Schulen weiter,
          damit sie dich verknüpfen können. Danach erscheinen hier ihre Einsätze.
        </p>
      </div>`;
    return;
  }

  if (resp.items.length === 0) {
    content.innerHTML = `
      <div class="empty-state empty-state--inline">
        <p style="margin:0">Keine Einsätze in diesem Zeitraum.</p>
      </div>`;
    return;
  }

  // Items nach Schule gruppieren (Reihenfolge: nach Schulname stabil aus dem Backend)
  const bySchool = new Map<number, { schoolName: string; items: PracticePlanningAssignment[] }>();
  for (const it of resp.items) {
    let bucket = bySchool.get(it.schoolId);
    if (!bucket) {
      bucket = { schoolName: it.schoolName, items: [] };
      bySchool.set(it.schoolId, bucket);
    }
    bucket.items.push(it);
  }

  const today = isoDate(new Date());
  const sections = [...bySchool.entries()].map(([_schoolId, { schoolName, items }]) => {
    // endDate ist der letzte Tag des Einsatzes; nur den Datumsanteil vergleichen
    const currentCount = items.filter(i => i.startDate.slice(0, 10) <= today && i.endDate.slice(0, 10) >= today).length;
    const rows = items.map(it => {
      const studentLabel = it.isFreeInput
        ? `<em>${esc(it.freeInputText || "Freitext")}</em>`
        : esc(it.studentName || "");
      const deptLabel = it.departmentShortName || it.departmentName || it.department;
      const color = it.departmentColor || "var(--gray-400)";
      const clickable = !it.isFreeInput;
      const rowAttrs = clickable
        ? ` class="pp-row-clickable" data-assignment-id="${it.id}" style="cursor:pointer" title="Schüler-Info anzeigen"`
        : "";
      return `
        <tr${rowAttrs}>
          <td><strong>${studentLabel}</strong></td>
          <td class="muted">${esc(it.className || "")}</td>
          <td><span class="dept-dot" style="background:${esc(color)}"></span>${esc(deptLabel)}</td>
          <td class="muted">${esc(fmtDate(it.startDate))} bis ${esc(fmtDate(it.endDate))}</td>
        </tr>`;
    }).join("");

    return `
      <details class="school-section">
        <summary>
          <span>${esc(schoolName)}</span>
          <span class="school-count">${items.length} Einsätze${currentCount > 0 ? ` · ${currentCount} aktuell` : ""}</span>
        </summary>
        <table style="width:100%;border-collapse:collapse">
          <thead>
            <tr>
              <th>Schüler</th>
              <th>Klasse</th>
              <th>Fachbereich</th>
              <th>Zeitraum</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </details>`;
  }).join("");

  content.innerHTML = sections;

  content.querySelectorAll<HTMLTableRowElement>("tr.pp-row-clickable").forEach(tr => {
    tr.addEventListener("click", () => {
      const id = Number(tr.dataset.assignmentId);
      if (Number.isFinite(id)) openAssignmentModal(id);
    });
  });
}

async function openAssignmentModal(assignmentId: number) {
  openModal("Schüler-Info", `<div class="spinner"></div>`);
  const body = modalBody();

  let data: PracticeOrgAssignmentSteckbrief;
  try {
    data = await api.practiceOrg.assignmentSteckbrief(assignmentId);
  } catch (err: any) {
    body.innerHTML = `<div class="alert alert-error">${esc(err.message || "Schüler-Info konnte nicht geladen werden.")}</div>`;
    return;
  }

  const infoRow = (label: string, value: string) => `
    <div style="display:flex;justify-content:space-between;gap:1rem;padding:.4rem 0;border-bottom:1px solid var(--gray-100)">
      <span style="color:var(--gray-600);font-size:.875rem">${esc(label)}</span>
      <span style="font-size:.95rem;text-align:right">${esc(value)}</span>
    </div>`;

  const steckbriefSection = (() => {
    if (data.released && data.steckbriefText && data.steckbriefText.trim()) {
      const upd = data.steckbriefUpdatedAt
        ? `<div style="font-size:.75rem;color:var(--gray-500);margin-top:.5rem">Zuletzt aktualisiert: ${esc(formatDateTime(data.steckbriefUpdatedAt))}</div>`
        : "";
      return `
        <div style="margin-top:1.25rem">
          <h3 style="font-size:1rem;font-weight:600;margin:0 0 .5rem">Steckbrief</h3>
          <div style="white-space:pre-wrap;padding:.75rem;border:1px solid var(--gray-200);border-radius:.375rem;background:var(--gray-50);font-size:.95rem;line-height:1.5">${esc(data.steckbriefText)}</div>
          ${upd}
        </div>`;
    }
    return `
      <div style="margin-top:1.25rem">
        <h3 style="font-size:1rem;font-weight:600;margin:0 0 .5rem">Steckbrief</h3>
        <div style="padding:.75rem;border:1px dashed var(--gray-300);border-radius:.375rem;color:var(--gray-600);font-size:.9rem">
          Der Schüler hat seinen Steckbrief nicht für Praktikumsstellen freigegeben.
        </div>
      </div>`;
  })();

  body.innerHTML = `
    <div>
      ${infoRow("Name", data.studentName)}
      ${infoRow("Klasse", data.className || "")}
      ${infoRow("Fachbereich", data.departmentName || "")}
      ${infoRow("Zeitraum", `${fmtDate(data.startDate)} bis ${fmtDate(data.endDate)}`)}
    </div>
    ${steckbriefSection}
  `;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
